
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "step-1---display-the-apply-with-seek-button"
    }}>{`Step 1 - Display the Apply with SEEK button`}</h1>
    <p>{`Apply with SEEK can be used by a candidate to pre-fill in two scenarios:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`When a candidate accesses your application form via a job advertisement on SEEK.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate accesses your application form from a different source or directly.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "candidate-arrives-from-seek"
    }}>{`Candidate arrives from SEEK`}</h2>
    <p>{`If the candidate has arrived from a `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`link-out`}</a>{` advertisement on SEEK, you will receive the token in a `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter appended to the query string of the posted `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationMethod/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{`, for example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`https://www.example.com/job/123445/?seek-token=93WyyF1h6eaeb58ccc384b0e8353daa3011f1ece
`}</code></pre>
    <p>{`The token should be stored on your system for use in the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` below or when submitting a Completion Signal to notify that an application has been completed within your recruitment software.
The token is unique to that specific candidate, job ad, and session, and should be stored only for the duration of the application process.`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.a031fd04036230f84a9748a56e2c1e00f66afef0.mmd.svg')} title="Full sequence" />
    <h2 {...{
      "id": "candidate-arrives-from-another-source"
    }}>{`Candidate arrives from another source`}</h2>
    <p>{`Where a candidate has arrived at your application form from another source (direct link, company career site etc.), the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter will not be present in the query string and should be omitted in the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` below.`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.f64669e3654c7007fef63e8a5f52dc5d5e891462.mmd.svg')} title="Full sequence" />
    <h2 {...{
      "id": "retrieving-the-apply-with-seek-link"
    }}>{`Retrieving the Apply with SEEK link`}</h2>
    <p>{`The Apply with SEEK button displayed on your application page must link to a specific authorization URL defined by SEEK.
The linked authorization page will allow the candidate to authenticate and authorize access to their profile for pre-filling your application form.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` returns the URL that must be used in your Apply with SEEK button to redirect the candidate to the SEEK authorization page.`}</p>
    <p>{`Your software should provide an accurate view of the country and language preferences of the candidate via the standard `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language"
      }}><inlineCode parentName="a">{`Accept-Language`}</inlineCode></a>{` HTTP request header.
We support multiple brands and languages across our markets,
so this enables us to tailor the Apply with SEEK experience so it remains familiar to the candidate.
See `}<a parentName="p" {...{
        "href": "/graphql/in-practice#content-localisation"
      }}>{`content localisation`}</a>{` for more information.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"query ($input: _unstable_ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      blue\\n      pink\\n      white\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":67,\"column\":1,\"offset\":3434},\"end\":{\"line\":79,\"column\":4,\"offset\":3696},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    // A URI that the candidate will be redirected to after completing their authorization.\\n    // This URI must be supplied to SEEK in advance to ensure the security of candidate data.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The SEEK-allocated identifier for the advertiser, which enables the OAuth consent form to\\n    // display their name (i.e. “ATS on behalf of Advertiser”).\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The SEEK-allocated identifier for the job ad.\\n    // If the role is not listed on SEEK, this field can be omitted.\\n    \\\"positionProfileId\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:297Z5sddy\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    // If no seek-token is available (i.e. the candidate did not arrive from SEEK),\\n    // this field can be omitted.\\n    \\\"seekToken\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":81,\"column\":1,\"offset\":3698},\"end\":{\"line\":102,\"column\":4,\"offset\":4706},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"blue\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-blue.png\\\",\\n      \\\"pink\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-pink.png\\\",\\n      \\\"white\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-white.png\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=123def\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":104,\"column\":1,\"offset\":4708},\"end\":{\"line\":116,\"column\":4,\"offset\":5142},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"query": true,
        "($input:": true,
        "_unstable_ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton:": true,
        "_unstable_applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "blue\\n": true,
        "pink\\n": true,
        "white\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":67,\"column\":1,\"offset\":3434},\"end\":{\"line\":79,\"column\":4,\"offset\":3696},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "A": true,
        "URI": true,
        "that": true,
        "the": true,
        "candidate": true,
        "will": true,
        "be": true,
        "redirected": true,
        "to": true,
        "after": true,
        "completing": true,
        "their": true,
        "authorization.\\n": true,
        "This": true,
        "must": true,
        "supplied": true,
        "SEEK": true,
        "in": true,
        "advance": true,
        "ensure": true,
        "security": true,
        "of": true,
        "data.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "The": true,
        "SEEK-allocated": true,
        "identifier": true,
        "for": true,
        "advertiser,": true,
        "which": true,
        "enables": true,
        "OAuth": true,
        "consent": true,
        "form": true,
        "to\\n": true,
        "display": true,
        "name": true,
        "(i.e.": true,
        "“ATS": true,
        "on": true,
        "behalf": true,
        "Advertiser”).\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "job": true,
        "ad.\\n": true,
        "If": true,
        "role": true,
        "is": true,
        "not": true,
        "listed": true,
        "SEEK,": true,
        "this": true,
        "field": true,
        "can": true,
        "omitted.\\n": true,
        "\\\"positionProfileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:297Z5sddy\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "query": true,
        "string": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "no": true,
        "available": true,
        "did": true,
        "arrive": true,
        "SEEK),\\n": true,
        "\\\"seekToken\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":81,\"column\":1,\"offset\":3698},\"end\":{\"line\":102,\"column\":4,\"offset\":4706},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"blue\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-blue.png\\\",\\n": true,
        "\\\"pink\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-pink.png\\\",\\n": true,
        "\\\"white\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-white.png\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "123def\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":104,\"column\":1,\"offset\":4708},\"end\":{\"line\":116,\"column\":4,\"offset\":5142},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: _unstable_ApplyWithSeekButtonInput!) {
  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      blue
      pink
      white
    }
    url
  }
}
`}</code></pre>
    <h3 {...{
      "id": "configuring-content-security-policy-csp-for-secure-image-loading"
    }}>{`Configuring Content Security Policy (CSP) for secure image loading`}</h3>
    <p>{`This section is for integration partners who need to adhere to strict Content Security Policies in their applications to securely load images from the SEEK API using a hardcoded source.`}</p>
    <p>{`Content Security Policy (CSP) is a security feature that helps prevent various types of attacks, including Cross-Site Scripting (XSS) and data injection attacks.`}</p>
    <p>{`Add the following directive to your Content Security Policy (CSP) to allow Apply with SEEK images from the SEEK API:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`Content-Security-Policy: img-src 'self'
https://integration.seek.com/some-path-for-assets/images/;
`}</code></pre>
    <p>{`This directive instructs the browser to only load images from your own domain (‘self’) and the ones under our `}<inlineCode parentName="p">{`images`}</inlineCode>{` directory.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;